import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { REQUEST_STATUSES } from "@/constants";

// Types
interface Element {
  id: number;
  type: string;
  status: string;
  scope: string;
  title?: string;
  url?: string | null;
  memoMethod?: string | null;
  linkDestination?: string;
  size?: string;
  shape?: string;
  color?: string;
  textColor?: string;
  showText?: boolean;
  positioning?: string;
  iconLink?: string | null;
  iconName?: string | null;
  xFrameOptions?: string | null;
  helperText?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  mediaItemId?: number | null;
  experienceId?: number | null;
  createdAt?: string;
  updatedAt?: string;
  createdById?: number | null;
  linkedId?: number | null;
  isEmbeddable?: boolean | null;
}

const defaultState = {
  data: [] as Element[],
  status: REQUEST_STATUSES.IDLE as REQUEST_STATUSES,
  err: null as string | null,
} as const;

interface ElementState {
  data: Element[];
  status: REQUEST_STATUSES;
  err: string | null;
}

interface ElementsState {
  [key: string]: ElementState;
}

interface SetElementsRequestStatusPayload {
  propertyId: string;
  experienceId: string | number;
  status: REQUEST_STATUSES;
}

interface SetElementsPayload {
  propertyId: string;
  experienceId: string | number;
  elements: Element[];
  status?: REQUEST_STATUSES;
  err?: string | null;
}

interface RefreshElementPayload {
  propertyId: string;
  experienceId: string | number;
  element: Element;
}

type ExperienceId = string | number | null | undefined;

interface DeleteElementPayload {
  propertyId: string;
  experienceId: string | number;
  elementId: number;
}

// Initial state structure
const initialState: ElementsState = {};

// Helper to create element state
const createElementState = (): ElementState => ({ ...defaultState });

// Helper to get state key
const getStateKey = (propertyId: string, experienceId: string | number): string => `${propertyId}${experienceId}`;

const elementsSlice = createSlice({
  name: "elements",
  initialState,
  reducers: {
    // Elements actions
    setElementsRequestStatus: (state, action: PayloadAction<SetElementsRequestStatusPayload>) => {
      const { propertyId, experienceId, status } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (!state[key]) {
        state[key] = createElementState();
      }
      state[key].status = status as REQUEST_STATUSES;
    },

    setElements: (state, action: PayloadAction<SetElementsPayload>) => {
      const { propertyId, experienceId, elements, status, err } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      state[key] = {
        data: elements || [],
        status: (status || REQUEST_STATUSES.SUCCEEDED) as REQUEST_STATUSES,
        err: err || null,
      };
    },

    refreshElement: (state, action: PayloadAction<RefreshElementPayload>) => {
      const { propertyId, experienceId, element } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (state[key]) {
        const index = state[key].data.findIndex((el: Element) => el.id === element.id);
        if (index !== -1) {
          state[key].data[index] = element;
        }
      }
    },

    deleteElement: (state, action: PayloadAction<DeleteElementPayload>) => {
      const { propertyId, experienceId, elementId } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (state[key]) {
        state[key].data = state[key].data.filter((el: Element) => el.id !== elementId);
      }
    },
  },
});

// Export actions
export const { setElementsRequestStatus, setElements, refreshElement, deleteElement } = elementsSlice.actions;

// Export reducer
export default elementsSlice.reducer;

// Memoized selectors
export const elementsSelector = (propertyId: string, experienceId: ExperienceId) =>
  createSelector(
    (state: { elements: ElementsState }) => {
      if (!experienceId) return undefined;
      return state.elements[getStateKey(propertyId, experienceId)];
    },
    (elementsData) => ({
      elements: elementsData?.data || [],
      status: elementsData?.status || REQUEST_STATUSES.IDLE,
      err: elementsData?.err || null,
    }),
  );
